import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { useOrgBranding, useOrgDocumentIdentity } from '@/components/OrgMark';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Shirt,
  Activity,
  Banknote,
  BellRing,
  BookText,
  Brain,
  Calendar,
  ChevronDown,
  ClipboardList,
  ChevronLeft,
  ChevronRight,
  Clock,
  Command as CommandIcon,
  DatabaseZap,
  PoundSterling,
  FileText,
  FileWarning,
  FlaskConical,
  Gauge,
  GraduationCap,
  Landmark,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Menu,
  MessageSquare,
  Moon,
  Network,
  Rocket,
  Route,
  Search,
  Settings,
  ShieldAlert,
  ShieldCheck,
  ClipboardCheck,
  Briefcase,
  Sparkles,
  Sun,
  TrendingDown,
  TrendingUp,
  Trophy,
  Truck,
  UserCheck,
  UserSquare,
  Megaphone,
  Users,
  Warehouse,
  Webhook,
  Wrench,
  X,
  Target,
  Send,
  CalendarX2,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSubcontractorProfile } from '@/contexts/SubcontractorProfileContext';
import { useTheme } from '@/contexts/ThemeContext';
import { supabase } from '@/lib/supabaseClient';
import { useToast } from '@/components/ui/use-toast';
import { useUnreadMessages } from '@/contexts/UnreadMessagesContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { FIELD_ASSIST_ENABLED } from '@/lib/featureFlags';
import useOrg from '@/contexts/OrgContext';
import InvoiceAutomationModal from '@/components/admin/invoicing/InvoiceAutomationModal';
import { fetchTrendMetrics, computeDowntrends } from '@/lib/crewTrends';
import AutoProcessedAlert from '@/components/admin/invoicing/AutoProcessedAlert';
import TopAlertStrip from '@/components/admin/TopAlertStrip';
import WhatsAppHeaderPill from '@/components/admin/WhatsAppHeaderSheet';
import BookingsHeaderPill from '@/components/admin/BookingsHeaderSheet';
import RouteAcceptanceModal from '@/components/admin/RouteAcceptanceModal';
import WorkOfferComposer from '@/components/admin/WorkOfferComposer';
import AdminPushTokenRefresher from '@/components/admin/AdminPushTokenRefresher';
import DemoNavPad from '@/components/admin/DemoNavPad';
import { FileCheck, UserCog } from 'lucide-react';
import { useAdminAccess } from '@/lib/adminAccess';
import { setSmartDash } from '@/components/smartdash/useSmartDash';
import { PlayCircle, Presentation } from 'lucide-react';
import { HeartPulse } from 'lucide-react';
import { HeartHandshake, Wallet } from 'lucide-react';
import { AtSign } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import ScreenCurtain, { CurtainToggleButton } from '@/components/admin/ScreenCurtain';
import { PresentationBackdrop, ProjectorStageDriver } from '@/components/admin/PresentationStage';
import StagePrivacyMask from '@/components/admin/StagePrivacyMask';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandShortcut,
} from '@/components/ui/command';

// Operational domains — each maps to a hover-rail icon with its own hue.
// Recruitment is its own domain rather than a single line under Compliance:
// every recruitment screen is named here and links straight to its tab.
// An item path may carry ?tab=, which isActive() matches on (see below).
const navGroups = [
  {
    title: 'Management',
    domainIcon: Network,
    hue: '#2563EB',
    items: [
      { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard', desc: 'The now, at a glance' },
      { icon: Warehouse, label: 'Depots', path: '/admin/dashboard/depots', desc: 'Sites & capacity' },
      { icon: Users, label: 'Crews', path: '/admin/dashboard/crews', desc: 'Teams on the road' },
      { icon: UserSquare, label: 'Subcontractors', path: '/admin/dashboard/subcontractors', desc: 'Driver directory' },
      { icon: Calendar, label: 'Calendars', path: '/admin/dashboard/calendars', desc: 'Coverage view' },
    ],
  },
  {
    title: 'Operations',
    domainIcon: Truck,
    hue: '#4F46E5',
    items: [
      { icon: ListChecks, label: 'Allocations', path: '/admin/dashboard/allocations', desc: 'Assign crews to routes' },
      { icon: Route, label: 'Routing', path: '/admin/dashboard/routing', desc: 'Route files & pairing' },
      { icon: BookText, label: 'Logging', path: '/admin/dashboard/logging', desc: 'What happened on road' },
      { icon: Clock, label: 'Time Requests', path: '/admin/dashboard/time-requests', desc: 'Additional time' },
      { icon: Shirt, label: 'Uniforms', path: '/admin/dashboard/uniforms', desc: 'Crew kit requests → depot order' },
      { icon: ClipboardList, label: 'Protocols', path: '/admin/dashboard/protocols', desc: 'Day playbooks crews opt into' },
      { icon: Briefcase, label: 'Additional Work', path: '/admin/dashboard/additional-work', desc: 'Van movements & extra jobs: who accepted' },
      { icon: CalendarX2, label: 'Cancellations', path: '/admin/dashboard/availability-cancellations', desc: 'Days given up, and the 48-hour override' },
      { icon: FileWarning, label: 'Terminations', path: '/admin/dashboard/terminations', desc: 'Breach letters, archive windows, full history' },
    ],
  },
  {
    title: 'Intelligence',
    domainIcon: Brain,
    hue: '#A21CAF',
    items: [
      { icon: Gauge, label: 'Analytics', path: '/admin/dashboard/analytics', desc: 'Network performance dashboard' },
      { icon: Brain, label: 'Intelligence Partner', path: '/admin/dashboard/ai-insights', desc: 'Anomalies & trends' },
      { icon: TrendingUp, label: 'Fleet Performance', path: '/admin/dashboard/performance', desc: 'Coaching & metrics' },
      { icon: FileText, label: 'Reports Archive', path: '/admin/dashboard/reports', desc: 'Sent reports & read status' },
      { icon: Trophy, label: 'National Insights', path: '/admin/dashboard/national-insights', desc: 'Benchmark vs the network' },
      ...(FIELD_ASSIST_ENABLED
        ? [
            { icon: ShieldAlert, label: 'Field Intelligence', path: '/admin/dashboard/field-intelligence', desc: 'Review & authorise Field Assist captures' },
            { icon: FlaskConical, label: 'Research Programme', path: '/admin/dashboard/research-programme', desc: 'Paid Field Assist research: controls & invites' },
          ]
        : []),
    ],
  },
  {
    title: 'Financials',
    domainIcon: PoundSterling,
    hue: '#059669',
    items: [
      { icon: Banknote, label: 'Accounting', path: '/accounting-hub/control', desc: 'Ledger, VAT & reconciliation' },
      { icon: FileText, label: 'Invoicing', path: '/admin/dashboard/invoicing', desc: 'Bill the network' },
      { icon: TrendingUp, label: 'Financials', path: '/admin/dashboard/financials', desc: 'P&L overview' },
      { icon: PoundSterling, label: 'Payment Schedules', path: '/admin/dashboard/payment-schedules', desc: 'Weekly pay runs' },
      { icon: Wallet, label: 'Pay run', path: '/admin/dashboard/pay-run', desc: 'This pay day, from agreed invoices' },
      { icon: HeartHandshake, label: 'Partnerships', path: '/admin/dashboard/partnerships', desc: 'Driver-supply partners & badges' },
    ],
  },
  {
    title: 'Compliance',
    domainIcon: ShieldCheck,
    hue: '#B45309',
    items: [
      { icon: FileCheck, label: 'Agreements', path: '/admin/dashboard/agreements', desc: 'Signed contracts' },
      { icon: FileWarning, label: 'Field Cases', path: '/admin/dashboard/field-cases', desc: 'PCNs & property damage' },
      { icon: Settings, label: 'Drivers Management', path: 'https://drivers.parcelink.org/management', desc: 'Driver acquisition site' },
    ],
  },
  {
    // Recruitment used to be one entry under Compliance with everything else
    // reachable only from unlabelled buttons inside it. Each screen is named
    // here and goes straight to its own tab, so the menu says what exists.
    title: 'Recruitment',
    domainIcon: UserCheck,
    hue: '#7C3AED',
    items: [
      { icon: Target, label: 'Candidate Pipeline', path: '/admin/dashboard/recruitment?tab=pipeline', desc: 'Everyone in the funnel' },
      { icon: Sparkles, label: 'Screening AI', path: '/admin/dashboard/recruitment?tab=settings', desc: 'Recruitment brain & scoring' },
      { icon: Search, label: 'Driver Finder', path: '/admin/dashboard/recruitment?tab=sourcing', desc: 'Source drivers & porters' },
      { icon: Send, label: 'Send Recruit List', path: '/admin/dashboard/recruitment?tab=recruit-lists', desc: 'Email new recruits to the depot' },
      { icon: ShieldCheck, label: 'Verifications', path: '/admin/dashboard/recruitment?tab=verification', desc: 'ID & right to work' },
      { icon: GraduationCap, label: 'Training Planner', path: '/admin/dashboard/recruitment?tab=training', desc: 'OTR training days' },
      { icon: MessageSquare, label: 'WhatsApp Rescue', path: '/admin/dashboard/recruitment?tab=rescue', desc: 'Win back stalled applicants' },
    ],
  },
  {
    title: 'Marketing',
    domainIcon: Megaphone,
    hue: '#DB2777',
    items: [
      { icon: Megaphone, label: 'Marketing Hub', path: '/admin/dashboard/marketing', desc: 'Landing, SEO, media & B2B pipeline' },
      { icon: Warehouse, label: 'Job Archive', path: '/admin/dashboard/jobs', desc: 'Job pages by region' },
    ],
  },
  {
    title: 'System',
    domainIcon: Wrench,
    hue: '#475569',
    items: [
      { icon: MessageSquare, label: 'Messages', path: '/admin/dashboard/messages', desc: 'Broadcasts & threads' },
      { icon: AtSign, label: 'Mail', path: '/admin/dashboard/mail', desc: 'The parcelink.org mailbox' },
      { icon: BellRing, label: 'Push Notifications', path: '/admin/dashboard/notifications', desc: 'Send a push' },
      { icon: Activity, label: 'Activity', path: '/admin/dashboard/activity', desc: 'Who did what' },
      { icon: Settings, label: 'Settings', path: '/admin/dashboard/settings', desc: 'Platform config' },
      { icon: DatabaseZap, label: 'Data Tools', path: '/admin/dashboard/data-tools', desc: 'Import / export' },
      { icon: Gauge, label: 'Engine Room', path: '/admin/dashboard/engine-room', desc: 'AI usage & cost presets' },
      { icon: HeartPulse, label: 'Platform Health', path: '/admin/dashboard/platform-health', desc: 'Banks, webhooks & cron status' },
      { icon: PlayCircle, label: 'Product Demos', path: '/admin/dashboard/product-demos', desc: 'Manage subcontractor guides' },
      { icon: Presentation, label: 'Decks', path: '/admin/dashboard/decks', desc: 'Present a deck in the room or remotely' },
      { icon: GraduationCap, label: 'Training Hub', path: 'https://logisticshub.learn.link', desc: 'Subcontractor training materials' },
      { icon: Webhook, label: 'Webhooks', path: '/admin/dashboard/webhooks', desc: 'Integrations' },
      { icon: Settings, label: 'Invoice Settings', path: '/admin/dashboard/invoice-settings', desc: 'Automation rules' },
    ],
  },
  {
    title: 'Team',
    domainIcon: UserCog,
    hue: '#0F766E',
    items: [
      { icon: UserCog, label: 'Team', path: '/admin/dashboard/team', desc: 'Who is in this portal, and what they can reach' },
    ],
  },
];

// Which access area each domain belongs to. Written out rather than derived
// from the title, because a permission boundary must not move when somebody
// renames a heading. A group whose title is not listed here is ALWAYS shown —
// failing open is deliberate: see src/lib/adminAccess.js.
const AREA_BY_TITLE = {
  Management: 'management',
  Operations: 'operations',
  Intelligence: 'intelligence',
  Financials: 'financials',
  Compliance: 'compliance',
  Recruitment: 'recruitment',
  Marketing: 'marketing',
  System: 'system',
  Team: 'team',
};

// Which area a URL belongs to, derived from navGroups so the route guard and
// the sidebar cannot disagree about it. Longest path first: /admin/dashboard
// is a prefix of everything, so a shorter match must never win.
const AREA_ROUTES = navGroups
  .flatMap((g) => (g.items || []).map((it) => ({
    path: it.path.split('?')[0],
    area: AREA_BY_TITLE[g.title] || null,
  })))
  .filter((r) => r.area && r.path.startsWith('/'))
  .sort((a, b) => b.path.length - a.path.length);

const areaForPath = (pathname) =>
  AREA_ROUTES.find((r) => pathname === r.path || pathname.startsWith(`${r.path}/`))?.area || null;

// A URL with no ?tab shows the first tab, so a nav item pointing at that tab
// must still count as active. Derived from navGroups rather than hard-coded, so
// adding a tab to a hub cannot leave the sidebar quietly out of step with it.
const defaultTabFor = (base) => {
  const first = navGroups
    .flatMap((g) => g.items)
    .find((it) => it.path.startsWith(`${base}?`));
  return first ? new URLSearchParams(first.path.split('?')[1]).get('tab') : null;
};

// Stage 07 of the onboarding line only belongs to an org that is still being
// onboarded. Parcelink has been running for years; "Get Parcelink running" in
// its own sidebar would be noise in a working product, so the entry is added
// for tenants and left out of the flagship.
const withSetup = (groups) =>
  groups.map((g) => (g.title !== 'Management' ? g : {
    ...g,
    items: [
      ...g.items,
      { icon: Rocket, label: 'Setup & go-live', path: '/admin/dashboard/setup', desc: 'Finish setting up, then go live' },
      { icon: Sparkles, label: 'Plan & parts', path: '/admin/dashboard/plan', desc: 'Your bill, your resale margin, what you could add' },
    ],
  }));

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile } = useSubcontractorProfile();
  const { toast } = useToast();
  const { theme, toggleTheme } = useTheme();
  const { isFlagship } = useOrg();
  const brand = useOrgBranding();
  useOrgDocumentIdentity();
  const { can, unrestricted } = useAdminAccess();
  // The sidebar reflects what this admin can reach. It is NOT the security
  // boundary — every screen behind these links is protected by RLS on the
  // server — so an unknown group stays visible rather than vanishing.
  const groups = useMemo(() => {
    const base = isFlagship ? navGroups : withSetup(navGroups);
    if (unrestricted) return base;
    return base.filter((g) => {
      const area = AREA_BY_TITLE[g.title];
      return !area || can(area);
    });
  }, [isFlagship, unrestricted, can]);
  const { unreadCount } = useUnreadMessages(); // Added this
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [flyGroup, setFlyGroup] = useState(null); // hovered rail domain -> flyout
  const [flyTop, setFlyTop] = useState(96);
  const flyTimer = useRef(null);
  const [pendingInvoiceCount, setPendingInvoiceCount] = useState(0);
  const [readyToPayCount, setReadyToPayCount] = useState(0);
  const [pendingTimeRequestCount, setPendingTimeRequestCount] = useState(0);
  const [outstandingAcceptances, setOutstandingAcceptances] = useState(0);
  const [isAcceptanceModalOpen, setIsAcceptanceModalOpen] = useState(false);
  const [isWorkOfferOpen, setIsWorkOfferOpen] = useState(false);
  const [newSignatureCount, setNewSignatureCount] = useState(0);
  const [pendingIdReviewCount, setPendingIdReviewCount] = useState(0);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [downtrendingCount, setDowntrendingCount] = useState(0);
  const [urgentRouteCount, setUrgentRouteCount] = useState(0);
  const [autoAlert, setAutoAlert] = useState(null); // { job, items }
  const alertSuppressed = useRef(localStorage.getItem('invoice_alert_suppressed') === 'true');
  const demoLiveRef = useRef(false);

  // Presenter escape hatch: while a demo session is live, ⌘-click (Ctrl-click
  // on Windows) ANYWHERE in the admin jumps straight back to the demo console.
  // Only armed during a live session so normal ⌘-click (open in new tab)
  // behaviour is untouched the rest of the time.
  useEffect(() => {
    let stop = false;
    const check = async () => {
      try {
        const { count } = await supabase
          .from('demo_sessions')
          .select('id', { count: 'exact', head: true })
          .neq('status', 'ended');
        if (!stop) demoLiveRef.current = (count ?? 0) > 0;
      } catch { /* keep last known state */ }
    };
    check();
    const poll = setInterval(check, 20000);
    const onClick = (e) => {
      if (!(e.metaKey || e.ctrlKey) || !demoLiveRef.current) return;
      e.preventDefault();
      e.stopPropagation();
      navigate('/admin/dashboard/product-demos');
    };
    document.addEventListener('click', onClick, { capture: true });
    return () => {
      stop = true;
      clearInterval(poll);
      document.removeEventListener('click', onClick, { capture: true });
    };
  }, [navigate]);

  // Fetch pending invoice items count for the global alert
  const fetchPendingInvoiceCount = async () => {
    try {
      const { count, error } = await supabase
        .from('pending_invoice_items')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'pending');

      if (!error) setPendingInvoiceCount(count || 0);
    } catch (error) {
      console.error("Error fetching pending count:", error);
    }
  };

  // Invoices the subcontractor has AGREED and that now just need paying —
  // the green header badge. Agreement is a column on Approved invoices
  // (migration 20261102); status stays Approved until Paid.
  const fetchReadyToPayCount = async () => {
    try {
      const { count, error } = await supabase
        .from('invoices')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'Approved')
        .not('agreed_at', 'is', null);
      if (!error) setReadyToPayCount(count || 0);
    } catch (error) {
      console.error('Error fetching ready-to-pay count:', error);
    }
  };

  const fetchPendingTimeRequestCount = async () => {
    try {
      // Logic for Indigo Header Badge:
      // Count items that are NOTified_depot_at null (New) 
      // OR items that were Sent in the last 24 hours (Recent Activity)
      const { count, error } = await supabase
        .from('additional_time_requests')
        .select('*', { count: 'exact', head: true })
        .is('notified_depot_at', null);
      
      if (!error) setPendingTimeRequestCount(count || 0);
    } catch (error) {
      console.error("Error fetching time request count:", error);
    }
  };

  // How many routes the crew has not yet confirmed they have. Counted from
  // the same RPC the modal renders, so the chip and the list can never
  // disagree about what is outstanding.
  const fetchOutstandingAcceptances = async () => {
    try {
      const { data, error } = await supabase.rpc('route_acceptance_status', { p_days: 3 });
      if (error) throw error;
      setOutstandingAcceptances(
        (data || []).filter((r) => (r.accepted ?? 0) < (r.recipients ?? 0)).length,
      );
    } catch (error) {
      // Leave the last known count rather than dropping to 0 — a failed read
      // must not look like "everything is accepted".
      console.error('Error fetching route acceptance state:', error?.message || error);
    }
  };

  const fetchNewSignatureCount = async () => {
    try {
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const { count, error } = await supabase
        .from('subcontractor_contracts')
        .select('*', { count: 'exact', head: true })
        .gte('signed_at', oneDayAgo);
      
      if (!error) setNewSignatureCount(count || 0);
    } catch (error) {
      console.error("Error fetching signature count:", error);
    }
  };

  const fetchPendingIdReviewCount = async () => {
    try {
      const { count, error } = await supabase
        .from('candidate_applications')
        .select('*', { count: 'exact', head: true })
        .eq('kyc_status', 'manual_review_required');
      
      if (!error) setPendingIdReviewCount(count || 0);
    } catch (error) {
      console.error("Error fetching ID review count:", error);
    }
  };

  // Inbound routes quarantined for admin acceptance (unknown crew identifier,
  // usually an external MCL). These need admin authority NOW — surfaced as the
  // URGENT header pill and kept live by a realtime subscription below.
  const fetchUrgentRouteCount = async () => {
    try {
      const { count, error } = await supabase
        .from('unmatched_inbound_routes')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'pending');
      if (!error) setUrgentRouteCount(count || 0);
    } catch (error) {
      console.error('Error fetching unmatched route count:', error);
    }
  };

  useEffect(() => {
    fetchUrgentRouteCount();
    const ch = supabase
      .channel('urgent-inbound-routes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'unmatched_inbound_routes' }, fetchUrgentRouteCount)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  // Downtrending-crews alert: crews whose weekly grading points are falling.
  const fetchDowntrendingCount = async () => {
    try {
      const { rows, dates } = await fetchTrendMetrics();
      setDowntrendingCount(computeDowntrends(rows, dates).length);
    } catch (error) {
      console.error('Error fetching downtrending crews:', error);
    }
  };

  useEffect(() => {
    fetchPendingInvoiceCount();
    fetchReadyToPayCount();
    fetchPendingTimeRequestCount();
    fetchOutstandingAcceptances();
    fetchNewSignatureCount();
    fetchPendingIdReviewCount();
    fetchDowntrendingCount();
    const interval = setInterval(() => {
        fetchPendingInvoiceCount();
        fetchReadyToPayCount();
        fetchPendingTimeRequestCount();
        fetchOutstandingAcceptances();
        fetchNewSignatureCount();
        fetchPendingIdReviewCount();
        fetchUrgentRouteCount();
    }, 30000);

    // Realtime: watch processing_queue completions for auto-approve alert
    const channel = supabase
      .channel('auto-invoice-alert')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'processing_queue',
        filter: 'status=eq.completed'
      }, async (payload) => {
        if (alertSuppressed.current) return;
        const job = payload.new;
        // Fetch the auto-approved items linked to this queue job
        const { data: items } = await supabase
          .from('pending_invoice_items')
          .select('crew_identifier, description, item_date, item_type, slip_total_net')
          .eq('source_email_id', job.source_log_id)
          .eq('status', 'approved')
          .filter('metadata->>auto_approved', 'eq', 'true');
        if (items && items.length > 0) {
          setAutoAlert({ job, items });
          fetchPendingInvoiceCount();
        }
      })
      .subscribe();

    return () => {
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, []);

  // ⌘K / Ctrl-K opens the command palette
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const profileName = useMemo(() => profile?.full_name || profile?.name || 'Admin user', [profile]);

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (_e) {
      /* already signed out */
    }
    try { window.localStorage.removeItem('parcelink_session_started_at'); } catch (_e) {}
    window.location.replace('/admin/login');
  };

  // A nav item may point at a tab (…/recruitment?tab=sourcing). Matching on
  // pathname alone would light every Recruitment entry at once, so the tab is
  // compared too — and a URL with no ?tab is showing the first tab, which is
  // what SectionedTabs renders.
  const isActive = (path) => {
    const [base, query] = path.split('?');
    const pathMatches = location.pathname === base
      || (base !== '/admin/dashboard' && location.pathname.startsWith(base));
    if (!pathMatches || !query) return pathMatches;
    const want = new URLSearchParams(query).get('tab');
    const have = new URLSearchParams(location.search).get('tab') || defaultTabFor(base);
    return want === have;
  };

  // Which domain + page is currently active (drives the rail highlight + breadcrumb)
  const activeGroup = useMemo(
    () => groups.find((g) => g.items.some((it) => !it.path.startsWith('http') && isActive(it.path))) || groups[0],
    [groups, location.pathname, location.search]
  );
  const activeItem = useMemo(
    () => activeGroup.items.find((it) => !it.path.startsWith('http') && isActive(it.path)),
    [activeGroup, location.pathname, location.search]
  );

  // Hover-rail flyout: open on domain hover, keep open while pointer is inside, close after a short grace period
  const openFly = (group, el) => {
    clearTimeout(flyTimer.current);
    if (el) {
      // Clamp by the panel's real estimated height (header + ~56px per item)
      // so long domains (System has 8 areas) never clip below the viewport;
      // the panel itself also scrolls as a fallback on short screens.
      const estHeight = Math.min(56 + group.items.length * 56 + 16, window.innerHeight - 24);
      setFlyTop(Math.max(12, Math.min(el.getBoundingClientRect().top, window.innerHeight - estHeight - 12)));
    }
    setFlyGroup(group);
  };
  const keepFly = () => clearTimeout(flyTimer.current);
  const closeFlySoon = () => { clearTimeout(flyTimer.current); flyTimer.current = setTimeout(() => setFlyGroup(null), 160); };

  const goTo = useCallback((path) => {
    setPaletteOpen(false);
    setFlyGroup(null);
    setMobileOpen(false);
    if (path.startsWith('http')) { window.open(path, '_blank', 'noopener'); return; }
    navigate(path);
  }, [navigate]);

  // Live badge count for a given nav path (mirrors the previous sidebar dots)
  const badgeFor = (path) => {
    if (path === '/admin/dashboard/messages' && Number(unreadCount) > 0) return { tone: 'indigo', count: null };
    if (path === '/admin/dashboard/agreements' && newSignatureCount > 0) return { tone: 'indigo', count: newSignatureCount };
    // IDs waiting for review are a Verifications job, so the badge sits on the
    // entry that opens that tab rather than on the whole hub.
    if (path === '/admin/dashboard/recruitment?tab=verification' && pendingIdReviewCount > 0) return { tone: 'rose', count: pendingIdReviewCount };
    if (path === '/admin/dashboard/time-requests' && pendingTimeRequestCount > 0) return { tone: 'indigo', count: pendingTimeRequestCount };
    return null;
  };
  const groupHasBadge = (group) => group.items.some((it) => badgeFor(it.path));

  const BadgePill = ({ b }) =>
    b ? (
      <span
        className={cn(
          'ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold',
          b.tone === 'rose' ? 'bg-rose-500/15 text-rose-500' : 'bg-indigo-500/15 text-indigo-500'
        )}
      >
        {b.count ?? '•'}
      </span>
    ) : null;

  return (
    // --domain-accent drives the .page-title pill tint, so each page's title
    // pill matches its rail domain colour (Operations indigo, Compliance brown…).
    <div
      className="flex min-h-screen bg-background text-foreground"
      style={{ '--domain-accent': activeGroup.hue }}
    >
      {/* ---- Desktop hover rail ---- */}
      <aside
        className="sticky top-0 z-40 hidden h-screen w-[70px] shrink-0 flex-col items-center gap-1.5 border-r border-border bg-card py-4 lg:flex"
        onMouseLeave={closeFlySoon}
      >
        <div className="mb-2 grid h-10 w-10 place-items-center rounded-xl bg-primary text-primary-foreground shadow-glow">
          <Sparkles className="h-5 w-5" />
        </div>
        {groups.map((g) => {
          const active = g.title === activeGroup.title;
          return (
            <button
              key={g.title}
              type="button"
              onMouseEnter={(e) => openFly(g, e.currentTarget)}
              onClick={() => goTo(g.items[0].path)}
              aria-label={g.title}
              className={cn(
                'relative grid h-11 w-12 place-items-center rounded-[13px] transition-colors',
                active ? 'text-white' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
              style={active ? { background: g.hue } : undefined}
            >
              <g.domainIcon className="h-5 w-5" />
              {groupHasBadge(g) && !active && (
                <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-rose-500" />
              )}
            </button>
          );
        })}
        <div className="mt-auto flex flex-col items-center gap-1.5">
          <button
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className="grid h-11 w-12 place-items-center rounded-[13px] text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          <button
            onClick={handleLogout}
            aria-label="Log out"
            className="grid h-11 w-12 place-items-center rounded-[13px] text-rose-400 hover:bg-rose-500/12"
          >
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </aside>

      {/* ---- Hover flyout ---- */}
      <AnimatePresence>
        {flyGroup && (
          <motion.div
            key={flyGroup.title}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -8 }}
            transition={{ duration: 0.14 }}
            onMouseEnter={keepFly}
            onMouseLeave={closeFlySoon}
            style={{ left: 74, top: flyTop }}
            className="premium-scrollbar fixed z-50 hidden max-h-[calc(100vh-24px)] w-[300px] overflow-y-auto rounded-2xl border border-border bg-popover p-2 shadow-shell lg:block"
          >
            <div className="flex items-center gap-2 px-3 py-2">
              <span className="h-2.5 w-2.5 rounded-full" style={{ background: flyGroup.hue }} />
              <span className="text-sm font-semibold text-foreground">{flyGroup.title}</span>
              <span className="ml-auto text-xs text-muted-foreground">{flyGroup.items.length} areas</span>
            </div>
            <div className="space-y-0.5">
              {flyGroup.items.map((it) => {
                const act = !it.path.startsWith('http') && isActive(it.path);
                return (
                  <button
                    key={it.path}
                    onClick={() => goTo(it.path)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors',
                      act ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'
                    )}
                  >
                    <span
                      className="grid h-8 w-8 shrink-0 place-items-center rounded-lg"
                      style={{ background: `color-mix(in srgb, ${flyGroup.hue} 14%, transparent)`, color: flyGroup.hue }}
                    >
                      <it.icon className="h-4 w-4" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{it.label}</span>
                      <span className="block truncate text-xs text-muted-foreground">{it.desc}</span>
                    </span>
                    <BadgePill b={badgeFor(it.path)} />
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ---- Mobile drawer ---- */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-slate-950/50 backdrop-blur-sm lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 30 }}
              className="fixed inset-y-0 left-0 z-50 flex w-[86vw] max-w-[320px] flex-col border-r border-border bg-card lg:hidden"
              style={{ paddingTop: 'env(safe-area-inset-top)' }}
            >
              <div className="flex items-center justify-between border-b border-border px-4 py-4">
                <div className="flex items-center gap-2.5">
                  <div className="grid h-9 w-9 place-items-center rounded-xl bg-primary text-primary-foreground">
                    <Sparkles className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{brand.wordmark}</p>
                    <p className="text-xs text-muted-foreground">Control tower</p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <div className="premium-scrollbar flex-1 overflow-y-auto px-3 py-4">
                {groups.map((g) => (
                  <div key={g.title} className="mb-5 last:mb-0">
                    <div className="mb-2 flex items-center gap-2 px-3">
                      <span className="h-2 w-2 rounded-full" style={{ background: g.hue }} />
                      <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">{g.title}</span>
                    </div>
                    <div className="space-y-1">
                      {g.items.map((it) => {
                        const act = !it.path.startsWith('http') && isActive(it.path);
                        return (
                          <button
                            key={it.path}
                            onClick={() => goTo(it.path)}
                            className={cn(
                              'flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm font-medium transition-colors',
                              act ? 'bg-primary text-primary-foreground' : 'text-foreground hover:bg-muted'
                            )}
                          >
                            <it.icon className="h-5 w-5 shrink-0" />
                            <span className="truncate">{it.label}</span>
                            <BadgePill b={badgeFor(it.path)} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 border-t border-border p-3">
                <Button variant="ghost" className="flex-1 justify-center text-foreground hover:bg-muted" onClick={toggleTheme}>
                  {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                  <span className="ml-2">{theme === 'dark' ? 'Light' : 'Dark'}</span>
                </Button>
                <Button variant="ghost" className="flex-1 justify-center text-rose-400 hover:bg-rose-500/12" onClick={handleLogout}>
                  <LogOut className="h-4 w-4" />
                  <span className="ml-2">Logout</span>
                </Button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ---- Content column ---- */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* pt: keep the bar below the iOS status bar/notch (viewport-fit=cover) */}
        <header
          className="sticky top-0 z-30 border-b border-border bg-background/80 backdrop-blur-xl"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          <div className="flex h-16 items-center gap-3 px-4 sm:px-6 lg:px-8">
            <Button variant="outline" size="icon" className="lg:hidden" onClick={() => setMobileOpen(true)}>
              <Menu className="h-5 w-5" />
            </Button>
            <div className="flex min-w-0 items-center gap-2">
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: activeGroup.hue }} />
              <span className="truncate text-sm font-semibold text-foreground">{activeGroup.title}</span>
              {activeItem && (
                <>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="truncate text-sm text-muted-foreground">{activeItem.label}</span>
                </>
              )}
            </div>

            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => setPaletteOpen(true)}
                className="hidden h-9 items-center gap-2 rounded-xl border border-border bg-card px-3 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground md:flex"
              >
                <Search className="h-4 w-4" />
                <span>Search or jump…</span>
                <kbd className="ml-1 rounded-md border border-border bg-muted px-1.5 text-[11px] font-semibold">⌘K</kbd>
              </button>
              <button
                onClick={() => setPaletteOpen(true)}
                aria-label="Search"
                className="grid h-9 w-9 place-items-center rounded-xl border border-border bg-card text-muted-foreground md:hidden"
              >
                <Search className="h-4 w-4" />
              </button>

              {urgentRouteCount > 0 && (
                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
                  <NavLink to="/admin/dashboard/routing">
                    <button
                      className="relative flex h-7 items-center gap-1.5 rounded-lg bg-rose-600 px-3 text-[11px] font-bold uppercase tracking-wide text-white shadow-sm transition-colors hover:bg-rose-700"
                      title={`${urgentRouteCount} inbound route${urgentRouteCount > 1 ? 's' : ''} waiting for admin acceptance`}
                    >
                      <span className="absolute -left-1 -top-1 flex h-2.5 w-2.5">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-75" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-500" />
                      </span>
                      <ShieldAlert className="h-3 w-3" />
                      <span>Urgent</span>
                      <span className="hidden normal-case tracking-normal lg:inline">· route acceptance</span>
                      <span className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-white text-[9px] font-bold text-rose-700">
                        {urgentRouteCount}
                      </span>
                    </button>
                  </NavLink>
                </motion.div>
              )}

              <WhatsAppHeaderPill />
              <BookingsHeaderPill />
              {downtrendingCount > 0 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <NavLink to="/admin/dashboard/performance/trends">
                    <button
                      className="flex items-center gap-1.5 px-3 h-7 rounded-lg border border-rose-200 bg-rose-50 text-[11px] font-semibold text-rose-700 hover:bg-rose-100 dark:border-rose-800/50 dark:bg-rose-900/20 dark:text-rose-400 transition-colors"
                    >
                      <TrendingDown className="h-3 w-3" />
                      <span className="hidden lg:inline">Downtrending</span>
                      <span className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-rose-600 text-[9px] font-bold text-white">
                        {downtrendingCount}
                      </span>
                    </button>
                  </NavLink>
                </motion.div>
              )}

              {pendingInvoiceCount > 0 && (
                <button
                  onClick={() => setIsInvoiceModalOpen(true)}
                  className="hidden h-9 items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 text-[11px] font-semibold text-amber-700 transition-colors hover:bg-amber-100 dark:border-amber-800/50 dark:bg-amber-900/20 dark:text-amber-400 sm:flex"
                >
                  <FileCheck className="h-3.5 w-3.5" />
                  <span className="hidden lg:inline">Invoices</span>
                  <span className="ml-0.5 grid h-4 w-4 place-items-center rounded-full bg-amber-600 text-[9px] font-bold text-white">{pendingInvoiceCount}</span>
                </button>
              )}
              {/* Agreed by the subcontractor, cleared to pay — the list the
                  payment run works from. */}
              {readyToPayCount > 0 && (
                <button
                  onClick={() => goTo('/admin/dashboard/invoicing?filter=ready-to-pay')}
                  className="hidden h-9 items-center gap-1.5 rounded-lg border border-green-200 bg-green-50 px-3 text-[11px] font-semibold text-green-700 transition-colors hover:bg-green-100 dark:border-green-800/50 dark:bg-green-900/20 dark:text-green-400 sm:flex"
                >
                  <FileCheck className="h-3.5 w-3.5" />
                  <span className="hidden lg:inline">Ready to pay</span>
                  <span className="ml-0.5 grid h-4 w-4 place-items-center rounded-full bg-green-600 text-[9px] font-bold text-white">{readyToPayCount}</span>
                </button>
              )}
              {pendingIdReviewCount > 0 && (
                <button
                  onClick={() => goTo('/admin/dashboard/recruitment?tab=verification&section=sessions')}
                  className="hidden h-9 items-center gap-1.5 rounded-lg border border-rose-200 bg-rose-50 px-3 text-[11px] font-semibold text-rose-700 transition-colors hover:bg-rose-100 dark:border-rose-800/50 dark:bg-rose-900/20 dark:text-rose-400 sm:flex"
                >
                  <ShieldCheck className="h-3.5 w-3.5" />
                  <span className="hidden lg:inline">ID Reviews</span>
                  <span className="ml-0.5 grid h-4 w-4 place-items-center rounded-full bg-rose-600 text-[9px] font-bold text-white">{pendingIdReviewCount}</span>
                </button>
              )}
              {pendingTimeRequestCount > 0 && (
                <button
                  onClick={() => goTo('/admin/dashboard/time-requests')}
                  className="hidden h-9 items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 text-[11px] font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 dark:border-indigo-800/50 dark:bg-indigo-900/20 dark:text-indigo-400 sm:flex"
                >
                  <Clock className="h-3.5 w-3.5" />
                  <span className="hidden lg:inline">Time</span>
                  <span className="ml-0.5 grid h-4 w-4 place-items-center rounded-full bg-indigo-600 text-[9px] font-bold text-white">{pendingTimeRequestCount}</span>
                </button>
              )}

              {outstandingAcceptances > 0 && (
                <button
                  onClick={() => setIsAcceptanceModalOpen(true)}
                  className="hidden h-9 items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 text-[11px] font-semibold text-amber-700 transition-colors hover:bg-amber-100 dark:border-amber-800/50 dark:bg-amber-900/20 dark:text-amber-400 sm:flex"
                  title="Routes the crew has not confirmed they have"
                >
                  <ClipboardCheck className="h-3.5 w-3.5" />
                  <span className="hidden lg:inline">Acceptance</span>
                  <span className="ml-0.5 grid h-4 w-4 place-items-center rounded-full bg-amber-600 text-[9px] font-bold text-white">{outstandingAcceptances}</span>
                </button>
              )}

              <button
                onClick={() => setIsWorkOfferOpen(true)}
                className="hidden h-9 items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50 px-3 text-[11px] font-semibold text-indigo-700 transition-colors hover:bg-indigo-100 dark:border-indigo-800/50 dark:bg-indigo-900/20 dark:text-indigo-400 sm:flex"
                title="Offer additional work to crew who are free"
              >
                <Briefcase className="h-3.5 w-3.5" />
                <span className="hidden lg:inline">Additional work</span>
              </button>

              {/* Only Parcelink reaches this frame now, via ?smartdash=off. */}
              <button
                type="button"
                onClick={() => setSmartDash(true)}
                className="hidden h-9 items-center rounded-full bg-foreground px-3 text-[11px] font-semibold text-background transition-opacity hover:opacity-85 md:flex"
              >
                Back to the new dash
              </button>
              <CurtainToggleButton />
              <Button variant="outline" size="icon" onClick={toggleTheme} className="hidden sm:inline-flex">
                {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              </Button>
              <div className="hidden rounded-xl border border-border bg-card px-3 py-1.5 text-right sm:block">
                <p className="text-sm font-semibold leading-tight text-foreground">{profileName}</p>
                {profile?.email && profile.email !== profileName && (
                  <p className="text-xs leading-tight text-muted-foreground">{profile.email}</p>
                )}
              </div>
            </div>
          </div>
        </header>

        <TopAlertStrip
          goTo={goTo}
          onOpenInvoices={() => setIsInvoiceModalOpen(true)}
          unreadCount={Number(unreadCount) || 0}
          pendingInvoiceCount={pendingInvoiceCount}
          pendingIdReviewCount={pendingIdReviewCount}
          pendingTimeRequestCount={pendingTimeRequestCount}
        />

        <main className="page-wrap flex-1 overflow-x-hidden">
          <div className="page-stack">
            {(() => {
              // Hiding a link is not access control — somebody can type the
              // URL. This refuses to render the screen. The REAL enforcement
              // is the restrictive RLS policies added in 20261241; this is the
              // courteous half, so a restricted admin gets an explanation
              // rather than an empty table and a console full of errors.
              const area = areaForPath(location.pathname);
              if (area && !can(area)) {
                return (
                  <div className="mx-auto max-w-lg px-4 py-20 text-center">
                    <h1 className="text-xl font-bold text-slate-900 dark:text-white">
                      You do not have access to this area
                    </h1>
                    <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                      Ask someone on your team with Team access to give you it, and this
                      page will appear in your menu.
                    </p>
                  </div>
                );
              }
              return children;
            })()}
          </div>
        </main>

        <AdminPushTokenRefresher />

        {/* The presenter's floating deck remote — renders only while a demo
            session is live, on every admin page. */}
        <DemoNavPad />

        <WorkOfferComposer
          open={isWorkOfferOpen}
          onOpenChange={setIsWorkOfferOpen}
        />

        <RouteAcceptanceModal
          open={isAcceptanceModalOpen}
          onOpenChange={setIsAcceptanceModalOpen}
        />

        {/* Privacy curtain — toggled from the header on any admin page */}
        <ScreenCurtain />

        {/* The presentation stage. PresentationBackdrop covers THIS screen in
            mirroring mode and lifts for scenes ticked "show my real screen";
            ProjectorStageDriver announces each scene's stage to the projector
            window, and — inside that window — keeps its heartbeat alive as it
            navigates to the real page a live scene asks for. */}
        <PresentationBackdrop />
        <ProjectorStageDriver />
        {/* Stage privacy — on the projector window (or a screen deliberately
            mirroring) with a session live, real personnel names in rendered
            text are masked to "First X."; the presenter's laptop never is. */}
        <StagePrivacyMask />

        <InvoiceAutomationModal
          isOpen={isInvoiceModalOpen}
          onClose={() => setIsInvoiceModalOpen(false)}
          onRefresh={fetchPendingInvoiceCount}
        />

        {autoAlert && (
          <AutoProcessedAlert
            job={autoAlert.job}
            items={autoAlert.items}
            onDismiss={() => setAutoAlert(null)}
            onDontShowAgain={() => {
              localStorage.setItem('invoice_alert_suppressed', 'true');
              alertSuppressed.current = true;
              setAutoAlert(null);
            }}
          />
        )}
      </div>

      {/* ---- Command palette (⌘K) ---- */}
      <CommandDialog open={paletteOpen} onOpenChange={setPaletteOpen}>
        <CommandInput placeholder="Search pages, or jump to a section…" />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          {groups.map((g) => (
            <CommandGroup key={g.title} heading={g.title}>
              {g.items.map((it) => (
                <CommandItem
                  key={it.path}
                  value={`${g.title} ${it.label} ${it.desc || ''}`}
                  onSelect={() => goTo(it.path)}
                >
                  <it.icon className="mr-2 h-4 w-4 text-muted-foreground" />
                  <span>{it.label}</span>
                  <CommandShortcut>{g.title}</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </div>
  );
};

// The Smart Dash frame reads the same menu, so access rules and labels have one source.
export { navGroups, AREA_BY_TITLE, areaForPath, defaultTabFor, withSetup };

export default AdminLayout;
